import {
  ApplicationConfig,
  importProvidersFrom,
  provideAppInitializer
} from '@angular/core';

import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';

import {provideAnimations} from '@angular/platform-browser/animations';
import {ReactiveFormsModule} from '@angular/forms';

/* ngx-translate */
import {
  TranslateModule,
  TranslateLoader,
  MissingTranslationHandler
} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';

/* Third‑party */
import {NgxMaskModule} from 'ngx-mask';
import {NgbModule, NgbTooltipConfig} from '@ng-bootstrap/ng-bootstrap';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatStepperModule} from '@angular/material/stepper';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {LoadingBarModule} from '@ngx-loading-bar/core';
import {LoadingBarHttpClientModule} from '@ngx-loading-bar/http-client';
import {LoadingBarRouterModule} from '@ngx-loading-bar/router';

/* Feature modules */
import {AppRoutingModule} from './app-routing.module';
import {CommonTudComponentsModule} from './common/common-tud-components.module';
import {LoginModule} from './login/login.module';
import {AdminModule} from './admin/admin.module';
import {ConsumerServicesModule} from './consumer-services/consumer-services.module';
import {BusinessServicesModule} from './business-services/business-services.module';
import {PageNotFoundModule} from './page-not-found/page-not-found.module';

/* App */
import {MyMissingTranslationHandler} from './missing-translation-handler';
import {JwtInterceptor} from './_interceptors/jwt.interceptor';
import {ErrorInterceptor} from './_interceptors/error.interceptor';
import {AppConfig} from './_data/config/app-config';
import {provideI18nInitializer} from './app.i18n.initializer';

export function httpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function appConfig(configuration: AppConfig): ApplicationConfig {
  return {
    providers: [
      /* Runtime app config */
      {
        provide: AppConfig,
        useValue: configuration
      },

      /* Core platform features */
      provideAnimations(),
      provideHttpClient(withInterceptorsFromDi()),

      /* Global tooltip config - openDelay prevents flicker caused by rapid mouseenter/mouseleave
         events as the cursor transitions between the button boundary and the fa-icon SVG child */
      {
        provide: NgbTooltipConfig,
        useFactory: () => {
          const config = new NgbTooltipConfig();
          config.openDelay = 100;
          config.closeDelay = 100;
          return config;
        }
      },

      /* NgModule interop */
      importProvidersFrom(
        ReactiveFormsModule,

        TranslateModule.forRoot({
          loader: {
            provide: TranslateLoader,
            useFactory: httpLoaderFactory,
            deps: [HttpClient]
          },
          missingTranslationHandler: {
            provide: MissingTranslationHandler,
            useClass: MyMissingTranslationHandler
          }
        }),

        NgxMaskModule.forRoot(),
        NgbModule,
        MatSnackBarModule,
        MatStepperModule,
        FontAwesomeModule,
        LoadingBarModule,
        LoadingBarHttpClientModule,
        LoadingBarRouterModule,

        /* Feature modules */
        AppRoutingModule,
        CommonTudComponentsModule,
        LoginModule,
        AdminModule,
        ConsumerServicesModule,
        BusinessServicesModule,
        PageNotFoundModule
      ),

      /* App initializer */
      provideAppInitializer(provideI18nInitializer()),

      /* Interceptors */
      {
        provide: HTTP_INTERCEPTORS,
        useClass: JwtInterceptor,
        multi: true
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorInterceptor,
        multi: true
      }
    ]
  };
}
